// ========================================
// WIZARD STATE & UTILITIES
// ========================================

// Wizard state
export const wizardState = {
    currentWizardStep: 1,
    currentHighlightMode: null,
    data: {
        comprehension: false,
        highlighting: {
            question: [],
            useful: [],
            distractor: []
        },
        representation: {
            bar1: 0,
            bar2: 0
        },
        reasoning: null,
        calculation: null,
        finalAnswer: null,
        errorCounts: {}
    }
};

// Expose to window for non-module scripts
window.wizardState = wizardState;

/**
 * Normaliza texto para comparaciones flexibles
 */
export function normalizeText(text) {
    if (!text) return '';
    return text
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // Elimina los diacríticos (acentos)
}

/**
 * Obtiene cadena traducida con reemplazos
 */
export function getString(key, placeholders = {}) {
    const langConfig = window.LANGUAGES ?
        (window.LANGUAGES[window.currentLanguage] || window.LANGUAGES['es-ES']) :
        { strings: {} };

    const strings = langConfig.strings || {};
    let str = strings[key];

    // Fallback for known keys if missing
    if (!str) {
        if (key === 'feedbackCalcWrong') str = 'Respuesta incorrecta, inténtalo de nuevo.';
        else if (key === 'feedbackCalcCorrect') str = '¡Correcto! Muy bien.';
        else if (key === 'representationNotAvailable') {
            const currentLang = window.currentLanguage || 'es-ES';
            if (currentLang.startsWith('en')) {
                str = '📊 Data representation not available (you must complete Step 3 to see the visual aid)';
            } else if (currentLang.startsWith('ca')) {
                str = '📊 Representació de dades no disponible (has de completar el Pas 3 per veure l\'ajuda visual)';
            } else if (currentLang.startsWith('eu')) {
                str = '📊 Datuen adierazpena ez dago erabilgarri (3. urratsa osatu behar duzu ikusizko laguntza ikusteko)';
            } else if (currentLang.startsWith('gl')) {
                str = '📊 Representación de datos non dispoñible (debes completar o Paso 3 para ver a axuda visual)';
            } else if (currentLang.startsWith('fr')) {
                str = '📊 Représentation des données non disponible (vous devez compléter l\'Étape 3 pour voir l\'aide visuelle)';
            } else if (currentLang.startsWith('de')) {
                str = '📊 Datendarstellung nicht verfügbar (Sie müssen Schritt 3 abschließen, um die visuelle Hilfe zu sehen)';
            } else if (currentLang.startsWith('it')) {
                str = '📊 Rappresentazione dei dati non disponibile (devi completare il Passo 3 per vedere l\'aiuto visivo)';
            } else if (currentLang.startsWith('pt')) {
                str = '📊 Representação de dados não disponível (deve completar o Passo 3 para ver a ajuda visual)';
            } else if (currentLang.startsWith('zh')) {
                str = '📊 数据表示不可用（您必须完成步骤3才能看到视觉辅助）';
            } else if (currentLang.startsWith('ar')) {
                str = '📊 تمثيل البيانات غير متاح (يجب عليك إكمال الخطوة 3 لرؤية المساعدة البصرية)';
            } else {
                str = '📊 Representación de datos no disponible (debes completar el Paso 3 para ver la ayuda visual)';
            }
        }
        else str = key;
    }

    // Replace placeholders like {value}, {number}, {name}, etc.
    for (const [placeholder, value] of Object.entries(placeholders)) {
        str = str.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), value);
    }

    return str;
}

export function getProblemStatement(problem) {
    if (!problem || !problem.statement) return '';
    if (typeof problem.statement === 'string') {
        return problem.statement;
    }
    const lang = window.currentLanguage || 'es-ES';
    return problem?.statement?.[lang] || problem?.statement?.['es-ES'] || Object.values(problem.statement || {})[0] || '';
}

/**
 * Extrae la pregunta del enunciado
 */
export function extractQuestionFromStatement(problem) {
    const text = getProblemStatement(problem);
    if (!text) return '';

    // Split by punctuation but keep it (using lookahead if needed, or simple split)
    // Simple split consumes delimiters.
    // Better: split and reconstruct or just regex match.
    // Existing logic was split(/[.?!]/).
    const sentences = text.split(/[.?!]/);

    // Look for sentence starting with question marks
    for (let s of sentences) {
        if (s.includes('¿') || s.includes('?')) {
            return (s + '?').trim();
        }
    }

    // Fallback: Last sentence
    return sentences[sentences.length - 1] || text;
}
/**
 * Determina si un problema es de resta de forma robusta
 */
export function isSubtraction(problem) {
    if (!problem) return false;

    // 1. PRIORIDAD MÁXIMA: Tipo explícito de la solución
    if (problem.solution && problem.solution.operation) {
        const op = problem.solution.operation.toLowerCase();
        if (op === 'subtraction' || op === 'resta' || op === 'sub') return true;
        if (op === 'addition' || op === 'suma' || op === 'add') return false;
    }

    // 2. PRIORIDAD MEDIA: Tipo del problema (Excel)
    if (problem.type) {
        const type = problem.type.toLowerCase();
        if (type.includes('resta') || type.includes('subtraction') || type.includes('sub') || type.includes('diff')) return true;
        if (type.includes('suma') || type.includes('addition') || type.includes('add')) return false;
    }

    // 3. FALLBACK: Sanity Check matemático (solo si lo anterior no es concluyente)
    if (problem.data && problem.data.useful && problem.data.useful.length >= 2) {
        const n1 = Math.max(...problem.data.useful);
        const n2 = Math.min(...problem.data.useful);
        const sol = problem.solution;
        const expected = (sol && sol.answer !== undefined) ? parseInt(sol.answer, 10) : null;

        // Si el resultado esperado coincide con la resta (y no con la suma), es probable que sea resta
        if (expected !== null && expected === (n1 - n2) && expected !== (n1 + n2)) {
            return true;
        }
    }

    return false;
}

/**
 * Sistema de pistas graduadas (Scaffolding)
 */
export function getFeedbackForAttempt(stepName, attemptCount, problem = null) {
    const isSub = isSubtraction(problem);
    
    switch(stepName) {
        case 'step2':
            if (attemptCount === 1) return '¡Casi! Vuelve a leer el texto despacio y prueba otra vez.';
            if (attemptCount === 2) return 'Fíjate bien en los números. Tienes que marcar la frase que tiene los números que necesitamos.';
            return 'Recuerda subrayar las frases que tienen los números y la pregunta principal.';
            
        case 'step4':
            if (attemptCount === 1) return '¡Cuidado! Fíjate bien en las barras.';
            if (attemptCount === 2) return 'Queremos encontrar el número MAYOR. Busca la barra que sea más alta.';
            return 'Compara el tamaño de las barras, la más grande representa el número mayor.';
            
        case 'step6':
            if (attemptCount === 1) return '¡Vaya! Esa no es la solución exacta. ¡Inténtalo de nuevo!';
            if (attemptCount === 2) {
                return isSub ? 'Recuerda que si hemos PERDIDO o QUITADO, tenemos que RESTAR. ¿Has restado bien?' : 'Recuerda que si hemos GANADO o JUNTADO, tenemos que SUMAR. ¿Has sumado bien?';
            }
            return 'Repasa el cálculo en el paso 5 con la tabla numérica o el cálculo mental para estar seguro.';
            
        default:
            return 'Inténtalo de nuevo. ¡Tú puedes!';
    }
}

// Expose to window
window.isSubtraction = isSubtraction;
window.getFeedbackForAttempt = getFeedbackForAttempt;
